import { QueryClient } from '@tanstack/react-query';
import { GlobalFormMessage } from '@tn/formbuilder/utils/schema-types';
import { refreshUserSpecificCarouselsAfterAuthChange } from '@tn/foundation/entities/hubs/utils/carousels/refreshUserSpecificCarouselsAfterAuthChange';
import { invalidatePPlusVideoAdUser } from '@tn/foundation/entities/video/hooks/usePPlusVideoAdUser';
import type useFullUserType from '@tn/foundation/hooks/providers/useFullUser';
import { cacheKey as USER_PROVIDER_CACHE_KEY } from '@tn/foundation/hooks/providers/useFullUser';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import {
    AUTH_COOKIE_KEY,
    FLOW_ID_SESSION_KEY,
    UNDEFINED,
} from '@tn/foundation/utils/constants/common';
import { currentEnvMirrorsProd } from '@tn/foundation/utils/getCurrentEnv';
import { matchRelevantPath } from '@tn/foundation/utils/matchRelevantPath';
import { invalidateMyListAfterAuthChange } from '@tn/foundation/utils/mylist/invalidateMyListAfterAuthChange';
import { resetSentimentAfterAuthChange } from '@tn/foundation/utils/sentiment/resetSentimentAfterAuthChange';
import {
    isAnonymousUser,
    isExSubscriberUser,
    isLoggedInUser,
    isRegisteredUser,
    isSubscriberUser,
} from '@tn/foundation/utils/user/user-predicates';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { NextRouter } from 'next/router';
import { IncomingMessage, ServerResponse } from 'node:http';
import { not, or, stateIn } from 'xstate';

import { decideFlowId } from '../hooks/helpers';
import { FlowId } from '../types';

/** Sent by `nativeBrowserNavigation` once a Next client-side route change completed. */
export const ROUTE_CHANGE = 'ROUTE_CHANGE';

// The events after which the route guards re-read the URL: machine start, browser
// back/forward, and a completed Next client-side route change (a `<Link>` from one
// route of the flow to another keeps the machine, so the URL has to be re-read).
// Any other event leaves the `always` transitions alone.
export const isRouteSyncEventGuard = or([
    ({ event }) => event.type === 'xstate.init',
    ({ event }) => event.type === 'POP_STATE',
    ({ event }) => event.type === ROUTE_CHANGE,
]);

type InvokeUrlObj = { invoke: { id: string; input: { url: string } }[] };
type RouteGuardPolicy = 'equality' | 'inclusion';

export const removeFlowId = () => sessionStorage.removeItem(FLOW_ID_SESSION_KEY);

type RouteGuardEvaluationArgs = {
    path: string;
    route: string;
    policy: RouteGuardPolicy;
    region: string;
    getConfigByKey: (key: string) => unknown;
};
const routeGuardEvaluation = ({
    path,
    route,
    policy,
    region,
    getConfigByKey,
}: RouteGuardEvaluationArgs) => {
    return matchRelevantPath(
        path,
        { path: route, exact: policy === 'equality' },
        region,
        getConfigByKey,
    );
};

// These guards check:
// - the above isRouteSyncEventGuard check
// - if the current state is not itself, to avoid looping
// - if the route is correct (the route is fetched from the configuration, it's in the input.url property of the first state under stateName)
type IsStateGuardArgs = {
    getConfigByKey: (key: string) => unknown;
    flowId: FlowId;
    router: NextRouter;
    region: string;
};
export const isStateGuard =
    ({ getConfigByKey, flowId, router, region }: IsStateGuardArgs) =>
    (stateName: string, policy: RouteGuardPolicy) => {
        const state = getConfigByKey(
            `${flowId}.machineConfig.states.${stateName}`,
        ) as InvokeUrlObj & {
            states: Record<string, InvokeUrlObj>;
            initial: string;
        };

        const route = !state?.states
            ? state?.invoke.find((inv) => inv.id === stateName)?.input?.url
            : (
                  state.states[state.initial as keyof typeof state.states] as InvokeUrlObj
              ).invoke.find((inv) => inv.id === stateName)?.input?.url;

        if (!route) {
            // No need for the guard if the route is not present
            return [() => false];
        }

        return [
            isRouteSyncEventGuard,
            not(stateIn(stateName)),
            () => {
                if (typeof window !== UNDEFINED) {
                    return routeGuardEvaluation({
                        path: window.location.pathname,
                        route,
                        policy,
                        region,
                        getConfigByKey,
                    });
                }

                return routeGuardEvaluation({
                    path: router.pathname,
                    route,
                    policy,
                    region,
                    getConfigByKey,
                });
            },
        ];
    };

export const getUser = (queryClient: QueryClient) =>
    queryClient.getQueryData<ReturnType<typeof useFullUserType>>([USER_PROVIDER_CACHE_KEY]);

export const ensureGetUser = async (queryClient: QueryClient) =>
    queryClient.ensureQueryData<ReturnType<typeof useFullUserType>>({
        queryKey: [USER_PROVIDER_CACHE_KEY],
    });

/** Invalidate and refetch the {@link useFullUser} query cache. */
export const refreshFullUserProfile = async (queryClient: QueryClient) => {
    await queryClient.invalidateQueries({ queryKey: [USER_PROVIDER_CACHE_KEY] });
    await queryClient.refetchQueries({ queryKey: [USER_PROVIDER_CACHE_KEY] });
    // Disjoint predicates (my-list owns isMyList rails), so the refetch rounds can overlap.
    await Promise.all([
        invalidateMyListAfterAuthChange(queryClient),
        refreshUserSpecificCarouselsAfterAuthChange(queryClient),
        invalidatePPlusVideoAdUser(queryClient),
    ]);
    await resetSentimentAfterAuthChange(queryClient);
};

export const getUserGuards = (queryClient: QueryClient) => {
    // Must stay a lazy read, not a snapshot: guards run at transition time and
    // the profile changes mid-flow (login, refreshFullUserProfile).
    const userFullProfile = () => getUser(queryClient)?.userFullProfile;
    return {
        // If user data is not yet loaded, default to anonymous (safe for redirects)
        isAnonymous: () => isAnonymousUser(userFullProfile()),
        isSubscriber: () => isSubscriberUser(userFullProfile()),
        isExSubscriber: () => isExSubscriberUser(userFullProfile()),
        isRegistered: () => isRegisteredUser(userFullProfile()),
        isRegisteredOrExSubscriber: or(['isRegistered', 'isExSubscriber']),
        hasCookie: () => {
            return !!getCookie(AUTH_COOKIE_KEY);
        },
        isLoggedIn: () => isLoggedInUser(userFullProfile()),
    };
};

export const removeFlowIdFromSession = (
    url: string,
    getConfigByKey: (key: string) => unknown,
    region: string,
) => {
    const flowId = decideFlowId(url, getConfigByKey, region);
    if (!flowId) {
        removeFlowId();
    }
};

/** Strip query, hash, and trailing slashes so route comparisons are stable. */
const normalizePathForRouteComparison = (path: string): string => {
    const pathOnly = path.split('?')[0].split('#')[0];
    const normalized = pathOnly.replace(/\/+$/, '');
    return normalized.length > 0 ? normalized : '/';
};

/**
 * True when `currentPath` already resolves to the flow route in `contextUrl`, including
 * region-prefixed paths (e.g. `/us/account/sign-in/` vs `/account/sign-in`).
 *
 * Outside prod only, also true for the route's `/mosaic-edit` sub-route (e.g. `/mosaic-signin/mosaic-edit`,
 * `/br/mosaic-signin/mosaic-edit`): the mosaic page editors live at `<public-route>/mosaic-edit` on
 * dev-gated surfaces, and the flow machine must not "correct" a visit to the editor back to
 * the public route. Prod behavior is unchanged — this never widens matching there.
 */
export const isAlreadyOnRoute = (currentPath: string, contextUrl: string): boolean => {
    const current = normalizePathForRouteComparison(currentPath);
    const route = normalizePathForRouteComparison(contextUrl);

    if (current === route) {
        return true;
    }

    const routeSegments = route.split('/').filter(Boolean);
    const currentSegments = current.split('/').filter(Boolean);

    if (routeSegments.length === 0 || currentSegments.length < routeSegments.length) {
        return false;
    }

    const suffix = currentSegments.slice(-routeSegments.length);
    if (suffix.join('/') === routeSegments.join('/')) {
        return true;
    }

    if (currentEnvMirrorsProd()) {
        return false;
    }

    const editSegments = [...routeSegments, 'mosaic-edit'];
    if (currentSegments.length < editSegments.length) {
        return false;
    }
    const editSuffix = currentSegments.slice(-editSegments.length);
    return editSuffix.join('/') === editSegments.join('/');
};

/**
 * Returns `contextUrl` when a redirect is needed, or `undefined` when the user is already
 * on that route.
 *
 * Client-side, use `window.location.pathname` because Next router state may point at an
 * in-flight destination and incorrectly suppress a required redirect. On the server,
 * fall back to `originalPathname`.
 */
export const getEvaluatedRoute = (
    originalPathname: string,
    contextUrl: string,
): string | undefined => {
    const currentPath = typeof window !== UNDEFINED ? window.location.pathname : originalPathname;
    return isAlreadyOnRoute(currentPath, contextUrl) ? undefined : contextUrl;
};

export const getErrorMessageFromEvent = (event: {
    error: { message: string } & { response: { errors: { message: string }[] } };
}): string => {
    return event.error.response?.errors?.[0]?.message || event.error.message || 'error';
};

const GRAPH_COOKIE = 'graph';
const GRAPH_COOKIE_FLOW_KEY = 'svod';
const GRAPH_COOKIE_CAMPAIGN_KEY = 'sv_campaign';
const CAMPAIGN_QUERY = 'cbscidmt';

type GraphCookieCampaign = {
    ftag: string;
    siteID: string;
    clickID: string;
    subID1: string;
    subID2: string;
    subID3: string;
    cbsClick: string;
    sharedID: string;
    promo: string;
    cbscidmt: string;
    contentID: string;
};

export const getLegacyCampaignCookieContent = (
    req?: IncomingMessage,
): Partial<GraphCookieCampaign> | null => {
    const legacyCampaignCookie = getCookie(GRAPH_COOKIE, { req });
    const campaignQuery =
        typeof window !== UNDEFINED
            ? new URLSearchParams(window.location.search).get(CAMPAIGN_QUERY)
            : null;

    if (!legacyCampaignCookie || typeof legacyCampaignCookie !== 'string') {
        return campaignQuery ? { cbscidmt: campaignQuery } : null;
    }

    let cookieContent: Record<string, unknown> | null = null;
    try {
        cookieContent = JSON.parse(legacyCampaignCookie);
    } catch {
        return campaignQuery ? { cbscidmt: campaignQuery } : null;
    }

    const content = cookieContent?.[GRAPH_COOKIE_CAMPAIGN_KEY] as
        | Partial<GraphCookieCampaign>
        | undefined;

    if (!content) {
        return campaignQuery ? { cbscidmt: campaignQuery } : null;
    }

    return campaignQuery ? { ...content, cbscidmt: campaignQuery } : content;
};

export type LegacyFlowInfo = Partial<{
    type: string;
    originUrl: string;
    cid: string;
    stepsCount: number;
    action: string;
    code: string;
}>;

const mapLegacyValues = (value?: string): LegacyFlowInfo | null => {
    if (!value) {
        return null;
    }

    let legacyArray: [string, string, string, number, string, string] | null = null;
    try {
        legacyArray = JSON.parse(value);
    } catch {
        return null;
    }

    return {
        type: legacyArray?.[0],
        originUrl: legacyArray?.[1],
        cid: legacyArray?.[2],
        stepsCount: legacyArray?.[3],
        action: legacyArray?.[4],
        code: legacyArray?.[5],
    };
};

const decodeBase64String = (encodedValue?: string): string => {
    if (!encodedValue) {
        return '';
    }

    try {
        if (typeof window !== UNDEFINED) {
            return atob(encodedValue);
        }
        return Buffer.from(encodedValue, 'base64').toString();
    } catch {
        return '';
    }
};

// Retrieves and decodes the legacy flow cookie content
// Returns null if the cookie is not present or cannot be decoded
// req: IncomingMessage - Useful only if this function is used server-side (e. g. middleware)
export const getLegacyFlowCookieContent = (req?: IncomingMessage): LegacyFlowInfo | null => {
    const legacyFlowCookie = getCookie(GRAPH_COOKIE, { req });
    if (legacyFlowCookie && typeof legacyFlowCookie === 'string') {
        let cookieContent: Record<string, string> | null = null;
        try {
            cookieContent = JSON.parse(legacyFlowCookie);
        } catch {
            return null;
        }
        const encodedValue = cookieContent?.[GRAPH_COOKIE_FLOW_KEY];
        return mapLegacyValues(decodeBase64String(encodedValue));
    }
    return null;
};

/**
 * Generates a cookie key for banner messages based on the route.
 * This makes the cookie name reusable across different routes.
 *
 * @param route - The route path (e.g., '/account', '/settings')
 * @returns The cookie key (e.g., 'account_banner_message', 'settings_banner_message')
 *
 * @example
 * getBannerMessageCookieKey('/account') // returns 'account_banner_message'
 * getBannerMessageCookieKey('/settings') // returns 'settings_banner_message'
 */
export const getBannerMessageCookieKey = (route: string): string => {
    // Remove leading/trailing slashes and replace remaining slashes with underscores
    const routeName = route.replace(/^\/+|\/+$/g, '').replace(/\//g, '_') || 'root';
    return `${routeName}_banner_message`;
};

/**
 * Sets a banner message cookie for a given route.
 * This cookie will be read and cleared in getServerSideProps.
 *
 * @param route - The route path (e.g., '/account', '/settings')
 * @param message - Translation key or message to display
 * @param variant - Banner variant (info, critical, success)
 *
 * @example
 * setBannerMessageCookie('/account', 'password_has_been_successfully_changed', 'info');
 */
export const setBannerMessageCookie = (
    route: string,
    message: string,
    variant: GlobalFormMessage['variant'] = 'info',
    /** Shown on /account when APIs may still return a transitional email (e.g. after updateEmail). */
    displayEmail?: string,
): void => {
    const cookieValue = JSON.stringify({
        message,
        variant,
        ...(displayEmail?.trim() ? { displayEmail: displayEmail.trim() } : {}),
    });

    setCookie(getBannerMessageCookieKey(route), cookieValue, {
        maxAge: 60, // 60 seconds - short-lived, just for passing data between navigation
        sameSite: 'lax',
        path: '/',
    });
};

/**
 * Reads and clears a banner message cookie for a given route.
 * This should be called in getServerSideProps to read the cookie and clear it after reading.
 *
 * @param route - The route path (e.g., '/account', '/settings')
 * @param req - HTTP request object (from getServerSideProps context)
 * @param res - HTTP response object (from getServerSideProps context)
 * @returns Banner message and variant, or null if no cookie exists or parsing fails
 *
 * @example
 * const bannerData = readAndClearBannerMessageCookie('/account', req, res);
 * if (bannerData) {
 *   // Use bannerData.message and bannerData.variant
 * }
 */
export const readAndClearBannerMessageCookie = (
    route: string,
    req: IncomingMessage,
    res: ServerResponse,
): { message: string; variant: GlobalFormMessage['variant']; displayEmail?: string } | null => {
    const cookieKey = getBannerMessageCookieKey(route);

    try {
        const cookieValue = getCookie(cookieKey, { req, res });

        if (!cookieValue || typeof cookieValue !== 'string') {
            return null;
        }

        const parsed = JSON.parse(cookieValue) as {
            message?: string;
            variant?: GlobalFormMessage['variant'];
            displayEmail?: string;
        };

        if (!parsed.message || typeof parsed.message !== 'string' || !parsed.message.trim()) {
            return null;
        }

        const message = parsed.message.trim();
        const variant: GlobalFormMessage['variant'] =
            parsed.variant && ['info', 'critical', 'success'].includes(parsed.variant)
                ? parsed.variant
                : 'info';

        const displayEmail =
            typeof parsed.displayEmail === 'string' && parsed.displayEmail.trim()
                ? parsed.displayEmail.trim()
                : undefined;

        // Clear the cookie after reading (one-time use)
        deleteCookie(cookieKey, { req, res, path: '/' });

        return { message, variant, ...(displayEmail ? { displayEmail } : {}) };
    } catch (error) {
        // If cookie parsing fails, ignore and return null
        appLogger.warn('Failed to parse banner message cookie', {
            error: error instanceof Error ? error.message : String(error),
        });
        return null;
    }
};
